import type { Filter, OperatorFilter, RangeFilter, ValueFilter } from 'json-to-lucene'

/**
 * Replace accepted status and quote date filter pair with accepted date filter
 */
export const sanitizeFiltersWithAcceptedDate = (filters: ValueFilter[]): Filter[] => {
  // Either value = 'accepted' or value = ['accepted', ...otherStatuses]
  const acceptedFilter = filters.find(
    (filter) =>
      filter.id === 'status.value' &&
      ((Array.isArray(filter.value) && filter.value.includes('accepted')) || filter.value === 'accepted')
  )

  const dateFilter = filters.find((filter) => filter.id === 'quote_date') as RangeFilter

  if (!acceptedFilter || !dateFilter) {
    return filters
  }

  const otherStatuses = Array.isArray(acceptedFilter.value)
    ? acceptedFilter.value.filter((value: string) => value !== 'accepted')
    : null

  // Remove the quote date and status filters
  const sanitizedFilters = filters.filter((filter) => filter.id !== 'quote_date' && filter.id !== 'status.value')

  // status is accepted and either status.history.accepted.timestamp or dates.client.accepted is in range
  const acceptedDateFilter: OperatorFilter[] = [
    {
      type: 'and',
      value: [
        {
          id: 'status.value',
          type: 'equal' as const,
          value: 'accepted'
        },
        {
          type: 'or' as const,
          value: [
            {
              id: 'status.history.accepted.timestamp',
              type: 'range' as const,
              value: dateFilter.value
            },
            {
              id: 'dates.client.accepted',
              type: 'range' as const,
              value: dateFilter.value
            }
          ]
        }
      ]
    }
  ]

  if (!otherStatuses?.length) {
    return [...sanitizedFilters, ...acceptedDateFilter]
  }

  return [
    ...sanitizedFilters,
    // Either status is accepted and the accepted date is in range,
    // or status is one of the other statuses and the quote_date is in range
    {
      type: 'or',
      value: [
        ...acceptedDateFilter,
        {
          type: 'and' as const,
          value: [
            {
              id: 'quote_date',
              type: 'range' as const,
              value: dateFilter.value
            },
            {
              type: 'or' as const,
              value: otherStatuses.map(
                (status) =>
                  ({
                    type: 'equal',
                    id: 'status.value',
                    value: status
                  }) as Filter
              )
            }
          ]
        }
      ]
    }
  ]
}
